// src/client/screens/borrower/HomeScreen.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft, ArrowRight, ArrowUpRight, Bell, ChevronRight,
  Eye, EyeOff, History as HistoryIcon, Send, TrendingUp,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useActiveLoan, useLoansHistory } from '@client/hooks/useLoan';
import { useCreditScore, useCreditScoreHistory } from '@client/hooks/useCreditScore';
import { useAuth } from '@client/auth/useAuth';
import { useToast } from '@client/components/ToastProvider';
import type { LoanFixture } from '@client/lib/fixtures';
import NavBar from '@client/components/NavBar';
import Pill from '@client/components/Pill';
import SectionLabel from '@client/components/SectionLabel';
import Stat from '@client/components/Stat';
import { kwachaToNgwee, type Ngwee } from '@/lib/types';
import { formatKwacha, ngweeToKwachaNumber } from '@client/utils/currency';
import { maxLoanForTier } from '@client/utils/creditScoreUtils';
import { formatDueIn, daysUntil } from '@client/utils/dates';

const REPAYABLE: LoanFixture['status'][] = ['active', 'delinquent', 'restructured'];
function isRepayable(loan: LoanFixture): boolean {
  return REPAYABLE.includes(loan.status);
}

// Local YYYY date label, matches the design's "Tuesday, Apr 28" pattern.
function todayLabel(): string {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'short',
  }).replace(',', ',');
}

// Synthesize an activity feed from the loan history. Disbursement = inflow,
// repayment = outflow. The real activity feed is a future server query.
interface Activity {
  dir: 'in' | 'out';
  label: string;
  sub: string;
  amountNgwee: Ngwee;
  ref: string;
  ts: number;
}
function activityFromLoans(loans: LoanFixture[]): Activity[] {
  const out: Activity[] = [];
  for (const l of loans) {
    if (l.disbursed_at) {
      out.push({
        dir: 'in',
        label: 'Loan disbursed',
        sub: new Date(l.disbursed_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short',
        }),
        amountNgwee: l.principal_ngwee,
        ref: l.loan_number,
        ts: new Date(l.disbursed_at).getTime(),
      });
    }
    if (l.repaid_at) {
      out.push({
        dir: 'out',
        label: 'Loan repaid',
        sub: new Date(l.repaid_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short',
        }),
        amountNgwee: l.total_due_ngwee,
        ref: l.loan_number,
        ts: new Date(l.repaid_at).getTime(),
      });
    }
  }
  return out.sort((a, b) => b.ts - a.ts).slice(0, 4);
}

// Reconstructs a score timeline from delta events and maps it to SVG coords.
// Falls back to a static upward curve when there are no events yet.
function buildSparkline(
  events: Array<{ delta: number }> | undefined,
  currentScore: number,
): { points: string; dots: [number, number][] } {
  const STATIC = {
    points: '0,38 14,34 28,30 42,32 56,24 70,22 84,16 98,12 110,8',
    dots: [[0,38],[14,34],[28,30],[42,32],[56,24],[70,22],[84,16],[98,12],[110,8]] as [number,number][],
  };
  if (!events || events.length === 0) return STATIC;

  // Events arrive newest-first; reverse for chronological order.
  const chron = [...events].reverse();
  const totalDelta = chron.reduce((s, e) => s + e.delta, 0);
  let running = Math.max(0, Math.min(100, currentScore - totalDelta));
  const scores: number[] = [];
  for (const e of chron) {
    running = Math.max(0, Math.min(100, running + e.delta));
    scores.push(running);
  }
  // Ensure the last point is exactly the current score.
  if (scores[scores.length - 1] !== currentScore) scores.push(currentScore);

  const W = 110, H = 48, pad = 4;
  const dots: [number, number][] = scores.map((s, i) => {
    const x = scores.length === 1 ? W / 2 : (i / (scores.length - 1)) * W;
    const y = H - pad - (s / 100) * (H - 2 * pad);
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10];
  });
  return { points: dots.map(([x, y]) => `${x},${y}`).join(' '), dots };
}

const HEADER_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  cursor: 'pointer',
  color: 'var(--c-text)',
};

export default function HomeScreen() {
  const { profile } = useAuth();
  const { data: loan } = useActiveLoan();
  const { data: score } = useCreditScore();
  const { data: scoreHistory } = useCreditScoreHistory();
  const { data: history } = useLoansHistory();
  const navigate = useNavigate();
  const toast = useToast();
  const [showBal, setShowBal] = useState(true);

  const first = profile?.full_name?.trim().split(/\s+/)[0];
  const greet = first ? `Hi, ${first}` : 'Hi there';

  // Limits + balances. If no score yet, fall back to "new" tier.
  const tier = score?.tier ?? 'new';
  const limitNgwee = maxLoanForTier(tier);
  const limitKwacha = ngweeToKwachaNumber(limitNgwee);
  const outstandingNgwee = (loan?.outstanding_ngwee ?? kwachaToNgwee(0));
  const drawnKwacha = ngweeToKwachaNumber(outstandingNgwee);
  const availableNgwee = (Math.max(
    0,
    Number(limitNgwee) - Number(outstandingNgwee),
  ) as Ngwee);
  const pct = limitKwacha > 0 ? (drawnKwacha / limitKwacha) * 100 : 0;

  const canBorrow = !loan || !isRepayable(loan);
  const canRepay = loan && isRepayable(loan);

  // Repayment timeline: 10 segments. Filled = paid portion of total_due.
  const filledSegments = (() => {
    if (!loan) return 0;
    const total = Number(loan.total_due_ngwee);
    if (total <= 0) return 0;
    const paid = total - Number(loan.outstanding_ngwee);
    return Math.round(Math.max(0, Math.min(1, paid / total)) * 10);
  })();

  const activity = activityFromLoans(history ?? []);

  return (
    <div className="app-shell app-shell--with-nav">
      <main className="scroll-area" style={{ padding: 0 }}>
        {/* Top bar */}
        <div style={{
          padding: '14px 24px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: 'var(--c-surface)',
              border: '1px solid var(--c-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 32 32" aria-hidden>
                <rect x="6" y="7"  width="20" height="4" rx="1" fill="#9CC5F0" />
                <rect x="6" y="14" width="20" height="4" rx="1" fill="#60A5E8" />
                <rect x="6" y="21" width="20" height="4" rx="1" fill="#2E82D6" />
              </svg>
            </div>
            <div>
              <div className="eyebrow" style={{ fontSize: 11, letterSpacing: 0.5 }}>{todayLabel()}</div>
              <div style={{ fontSize: 15, color: 'var(--c-text)', fontWeight: 500, marginTop: 2 }}>
                {greet}
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Notifications"
            style={HEADER_BTN}
            onClick={() => toast.show('No new notifications', 'info')}
          >
            <Bell size={18} />
            <span style={{
              position: 'absolute', top: 8, right: 8,
              width: 7, height: 7, borderRadius: 4,
              background: 'var(--c-accent)',
            }} aria-hidden />
          </button>
        </div>

        {/* HERO — available credit */}
        <div style={{ padding: '32px 24px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="eyebrow">Available to borrow</span>
            <button
              type="button"
              onClick={() => setShowBal((v) => !v)}
              aria-label={showBal ? 'Hide balance' : 'Show balance'}
              style={{
                background: 'transparent', border: 'none',
                cursor: 'pointer', color: 'var(--c-text-muted)', padding: 0,
              }}
            >
              {showBal ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </div>
          <div style={{
            fontSize: 76,
            fontWeight: 400,
            lineHeight: 1,
            color: 'var(--c-text)',
            letterSpacing: -3,
            marginTop: 6,
          }}>
            {showBal ? (
              <>
                {formatKwacha(availableNgwee, { decimals: 0 }).replace('K ', 'K')}
                <span style={{ fontSize: 36, color: 'var(--c-text-muted)', letterSpacing: -1 }}>.00</span>
              </>
            ) : 'K•••••'}
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--c-text-muted)' }}>
            of {formatKwacha(limitNgwee, { decimals: 0 })} limit
          </div>

          {/* Limit bar */}
          <div style={{
            marginTop: 18, height: 6,
            background: 'var(--c-chart-track)',
            borderRadius: 3, overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.max(0, Math.min(100, pct))}%`,
              height: '100%',
              background: 'var(--c-accent)',
            }} />
          </div>
          <div style={{
            marginTop: 8,
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'var(--ff-mono)',
            fontSize: 11,
            color: 'var(--c-text-muted)',
          }}>
            <span>K{drawnKwacha.toLocaleString()} drawn</span>
            <span>K{Math.max(0, limitKwacha - drawnKwacha).toLocaleString()} headroom</span>
          </div>
        </div>

        {/* Action row */}
        <div style={{
          padding: '0 24px',
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: 8,
        }}>
          <button
            type="button"
            disabled={!canBorrow}
            onClick={() => navigate(ROUTES.loanApply)}
            style={{
              background: canBorrow ? 'var(--c-accent)' : 'var(--c-surface-alt)',
              color: canBorrow ? 'var(--c-accent-ink)' : 'var(--c-text-dim)',
              border: 'none',
              borderRadius: 14,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: canBorrow ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--ff-sys)',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            <span>Borrow</span> <ArrowUpRight size={18} />
          </button>
          <button
            type="button"
            disabled={!canRepay}
            onClick={() => navigate(ROUTES.repay)}
            style={{
              background: 'transparent',
              border: '1px solid var(--c-border)',
              color: canRepay ? 'var(--c-text)' : 'var(--c-text-dim)',
              borderRadius: 14,
              padding: '14px 12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: canRepay ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--ff-sys)',
              fontWeight: 500,
              fontSize: 13,
              gap: 4,
            }}
          >
            <Send size={16} />
            Repay
          </button>
          <button
            type="button"
            onClick={() => navigate(ROUTES.loansList)}
            style={{
              background: 'transparent',
              border: '1px solid var(--c-border)',
              color: 'var(--c-text)',
              borderRadius: 14,
              padding: '14px 12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: 'pointer',
              fontFamily: 'var(--ff-sys)',
              fontWeight: 500,
              fontSize: 13,
              gap: 4,
            }}
          >
            <HistoryIcon size={16} />
            History
          </button>
        </div>

        {/* Active loan strip */}
        {loan && isRepayable(loan) && (
          <>
            <div style={{ padding: '0 24px' }}>
              <SectionLabel right={loan.loan_number}>Active loan</SectionLabel>
            </div>
            <div style={{ padding: '0 24px' }}>
              <div style={{
                background: 'var(--c-surface)',
                border: '1px solid var(--c-border)',
                borderRadius: 18,
                padding: 18,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="eyebrow" style={{ letterSpacing: 0.6 }}>Outstanding</div>
                    <div style={{
                      fontSize: 38,
                      fontWeight: 400,
                      color: 'var(--c-text)',
                      marginTop: 2,
                      letterSpacing: -1,
                      lineHeight: 1,
                    }}>
                      {formatKwacha(loan.outstanding_ngwee, { decimals: 0 })}
                      <span style={{ fontSize: 20, color: 'var(--c-text-muted)' }}>.00</span>
                    </div>
                  </div>
                  {loan.due_at && (
                    <Pill tone={daysUntil(loan.due_at) < 0 ? 'danger' : 'warning'}>
                      {formatDueIn(loan.due_at)}
                    </Pill>
                  )}
                </div>

                {/* Repayment timeline */}
                <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {Array.from({ length: 10 }, (_, i) => (
                    <div key={i} style={{
                      flex: 1, height: 4, borderRadius: 2,
                      background: i < filledSegments ? 'var(--c-accent)' : 'var(--c-chart-track)',
                    }} />
                  ))}
                </div>
                <div style={{
                  marginTop: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: 'var(--c-text-muted)',
                  fontFamily: 'var(--ff-mono)',
                }}>
                  <span>{formatKwacha((Number(loan.total_due_ngwee) - Number(loan.outstanding_ngwee)) as Ngwee, { decimals: 0 })} paid</span>
                  <span>{formatKwacha(loan.total_due_ngwee, { decimals: 0 })} total</span>
                </div>

                <div style={{
                  marginTop: 18, paddingTop: 16,
                  borderTop: '1px solid var(--c-border-lt)',
                  display: 'flex', gap: 8,
                }}>
                  <button
                    type="button"
                    onClick={() => navigate(ROUTES.repay)}
                    style={{
                      flex: 1,
                      background: 'var(--c-text)',
                      color: 'var(--c-bg)',
                      border: 'none',
                      borderRadius: 12,
                      padding: '12px',
                      fontFamily: 'var(--ff-sys)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Pay now
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(ROUTES.repay)}
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--c-border)',
                      color: 'var(--c-text)',
                      borderRadius: 12,
                      padding: '12px 16px',
                      fontFamily: 'var(--ff-sys)',
                      fontWeight: 500,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Schedule
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Credit standing */}
        {score && (
          <>
            <div style={{ padding: '0 24px' }}>
              <SectionLabel right={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <TrendingUp size={12} color="var(--c-success)" /> tier {score.tier}
                </span>
              }>
                Credit standing
              </SectionLabel>
            </div>
            <div style={{ padding: '0 24px' }}>
              <div style={{
                background: 'var(--c-surface)',
                border: '1px solid var(--c-border)',
                borderRadius: 18,
                padding: 20,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{
                      fontSize: 64,
                      fontWeight: 400,
                      color: 'var(--c-text)',
                      letterSpacing: -2,
                      lineHeight: 1,
                    }}>
                      {score.score}
                      <span style={{ color: 'var(--c-text-dim)', fontSize: 28, fontWeight: 400 }}>/100</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--c-text)', fontWeight: 500, marginTop: 6 }}>
                      {score.tier === 'new' ? 'New member' :
                       score.tier === 'building' ? 'Building credit' :
                       score.tier === 'established' ? 'Healthy borrower' :
                       score.tier === 'strong' ? 'Strong standing' :
                       'Excellent standing'}
                    </div>
                  </div>
                  {/* Live sparkline from credit_score_events */}
                  {(() => {
                    const spark = buildSparkline(scoreHistory, score.score);
                    return (
                      <svg width="110" height="48" viewBox="0 0 110 48" aria-label="Credit score history">
                        <polyline
                          points={spark.points}
                          fill="none"
                          stroke="var(--c-accent)"
                          strokeWidth="1.6"
                        />
                        {spark.dots.map(([x, y], i) => (
                          <circle key={i} cx={x} cy={y} r="1.5" fill="var(--c-accent)" />
                        ))}
                      </svg>
                    );
                  })()}
                </div>

                <div style={{
                  marginTop: 18,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 12,
                  paddingTop: 16,
                  borderTop: '1px solid var(--c-border-lt)',
                }}>
                  <Stat label="Loans" value={(history ?? []).length} mono />
                  <Stat label="On time" value={
                    (() => {
                      const repaid = (history ?? []).filter((l) => l.status === 'repaid').length;
                      const total = (history ?? []).length;
                      if (!total) return '—';
                      return `${Math.round((repaid / total) * 100)}%`;
                    })()
                  } mono />
                  <Stat
                    label="Tier limit"
                    value={`K${limitKwacha.toLocaleString()}`}
                    mono
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Recent activity */}
        {activity.length > 0 && (
          <>
            <div style={{ padding: '0 24px' }}>
              <SectionLabel right={
                <span
                  onClick={() => navigate(ROUTES.loansList)}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2 }}
                >
                  See all <ChevronRight size={11} />
                </span>
              }>
                Activity
              </SectionLabel>
            </div>
            <div style={{ padding: '0 24px' }}>
              {activity.map((tx, i) => (
                <div
                  key={`${tx.ref}-${tx.dir}-${tx.ts}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '16px 0',
                    borderBottom: i < activity.length - 1
                      ? '1px solid var(--c-border-lt)'
                      : 'none',
                  }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 12,
                    background: tx.dir === 'in' ? 'var(--c-accent-soft)' : 'var(--c-surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {tx.dir === 'in'
                      ? <ArrowDownLeft size={16} color="var(--c-accent)" />
                      : <ArrowUpRight size={16} color="var(--c-text)" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: 'var(--c-text)', fontWeight: 500 }}>
                      {tx.label}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>
                      {tx.sub}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontFamily: 'var(--ff-mono)',
                      fontSize: 14,
                      color: 'var(--c-text)',
                      fontWeight: 500,
                    }}>
                      {tx.dir === 'in' ? '+' : '−'}{formatKwacha(tx.amountNgwee, { decimals: 0 })}
                    </div>
                    <div style={{
                      fontFamily: 'var(--ff-mono)',
                      fontSize: 10,
                      color: 'var(--c-text-dim)',
                      marginTop: 2,
                    }}>
                      {tx.ref}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* No-loan empty state — only when there's no active loan AND no history */}
        {!loan && activity.length === 0 && (
          <div style={{ padding: '32px 24px' }}>
            <button
              type="button"
              onClick={() => navigate(ROUTES.loanApply)}
              style={{
                width: '100%',
                background: 'var(--c-surface)',
                border: '1px dashed var(--c-border)',
                borderRadius: 18,
                padding: '24px',
                cursor: 'pointer',
                textAlign: 'center',
                color: 'var(--c-text-sec)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <ArrowRight size={18} color="var(--c-accent)" />
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--c-text)' }}>
                Apply for your first loan
              </span>
              <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                K500–K{limitKwacha.toLocaleString()} · approved in minutes
              </span>
            </button>
          </div>
        )}

        <div style={{ height: 24 }} />
      </main>

      <NavBar />
    </div>
  );
}
